import { AsyncPipe, DatePipe } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SiaAIChatService } from '@sia/ai';
import {
  BytesConverterPipe,
  copyToClipboard,
  FilePreviewPipe,
  getFileTypeAllowed,
  SiaColorContrastAutoDirective,
  SiaTooltipDirective
} from '@sia/shared';
import { Subscription } from 'rxjs';
import { SiaGridFile } from '../../interfaces/grid-file';
import { FormatExtractedValuePipe } from '../../pipes/format-extracted-value.pipe';
import { HasExtractedValuePipe } from "../../pipes/has-extracted-value.pipe";
import { TruncateFilenamePipe } from '../../pipes/truncate-filename.pipe';
import { SiaAIFilesService } from '../../services/ai-files.service';

@Component({
  selector: 'sia-ai-grid-file',
  imports: [
    TruncateFilenamePipe,
    MatIcon,
    MatIconButton,
    FormatExtractedValuePipe,
    BytesConverterPipe,
    FilePreviewPipe,
    MatProgressSpinner,
    SiaColorContrastAutoDirective,
    SiaTooltipDirective,
    AsyncPipe,
    DatePipe,
    HasExtractedValuePipe
  ],
  templateUrl: './ai-grid-file.component.html',
  styleUrl: './ai-grid-file.component.scss',
})
export class AiGridFileComponent {
  #aiChatService = inject(SiaAIChatService);
  #siaAIFilesService = inject(SiaAIFilesService);
  #elementRef = inject(ElementRef);
  #sub: Subscription[] = [];
  checkbox = viewChild<HTMLInputElement>('checkbox');
  file = model<SiaGridFile>();
  viewMode = input<'dossier' | 'standard'>('standard');
  isRelevant = input<boolean>(false);
  isAnimatingIn = input<boolean>(false);
  addFileToDossier = output<SiaGridFile>();
  selectionChange = output<{ file: SiaGridFile, checked: boolean }>();
  onRowClicked = output<SiaGridFile>();
  onImgClicked = output<SiaGridFile>();
  copyToClipboard = copyToClipboard;
  isExpanded = signal<boolean>(false);
  isSelected = input<boolean>(false);
  isChecked = model<boolean>(false);
  isAnimatingOut = signal<boolean>(false);

  #isCheckedEffect = effect(() => {
    if (this.checkbox()) {
      this.checkbox()!.checked = this.isChecked();
    }
  });
  getExtraCategoriesText(): string {
    const categories = this.file()?.categories;
    if (!categories || categories.length <= 2) {
      return '';
    }

    const extraCategories = categories.slice(2);
    return extraCategories
      .map((category) => category?.description || category?.id || '')
      .filter((value) => !!value)
      .join(', ');
  }

  getExtractedPropertiesEntries(
    properties: Record<string, any> | undefined,
  ): { key: string; value: any }[] {
    if (!properties) {
      return [];
    }

    return Object.entries(properties)
      .map(([key, value]) => ({
        key,
        value: value,
      }))
      .sort((a, b) => {
        const orderA = a.value?.order_index;
        const orderB = b.value?.order_index;

        if (orderA === undefined && orderB === undefined) {
          return 0;
        }
        if (orderA === undefined) {
          return -1;
        }
        if (orderB === undefined) {
          return 1;
        }
        return orderA - orderB;
      });
  }

  toggleSelection() {
    this.isChecked.set(!this.isChecked());
    this.selectionChange.emit({ file: this.file()!, checked: this.isChecked() });
  }

  onRowClick() {
    if (this.viewMode() === 'dossier') {
      return;
    }
    this.onRowClicked.emit(this.file()!);
  }

  onImgClick() {
    const fileVal = this.file();
    if (!fileVal) return;

    const typeOrName = (fileVal.type ?? fileVal.name)?.toLocaleLowerCase();
    const allowedType = getFileTypeAllowed(fileVal.type, fileVal.name);

    if (allowedType !== '') {
      this.file.set({ ...fileVal, previewFileType: allowedType });
      this.onImgClicked.emit(this.file()!);
    } else {
      this.downloadFile();
    }
  }

  downloadFile() {
    const sha256 = this.file()?.sha256;
    let name = this.file()?.name;
    // if name dont have extension, add it from fileType
    if (name && !name.includes('.')) {
      const fileType = this.file()?.type;
      if (fileType) {
        name = name + '.' + fileType;
      }
    }
    if (sha256) {
      this.#aiChatService.downloadDocument(sha256, name || 'download');
    }
  }

  async retriveAdditionalInformations() {
    if (!this.file()!.completeExtractedProperties) {
      this.#siaAIFilesService
        .retrieveFileProperties(this.file()!.sha256)
        .then(async (extracetdProperties) => {
          this.file()!.completeExtractedProperties = extracetdProperties ?? {};
        });
    }
  }

  async toggleExpanded() {
    await this.retriveAdditionalInformations();
    this.isExpanded.set(!this.isExpanded());
  }

  ngOnInit() {
    // Applica la classe di animazione in entrata da sinistra se necessario
    // (quando il file ritorna dal dossier alla griglia)
    if (this.isAnimatingIn()) {
      this.#elementRef.nativeElement.classList.add('slide-in-from-left');
      setTimeout(() => {
        this.#elementRef.nativeElement.classList.remove('slide-in-from-left');
      }, 400);
    }
  }

  addFile() {
    // Avvia l'animazione di uscita verso destra
    this.isAnimatingOut.set(true);
    this.#elementRef.nativeElement.classList.add('slide-out-right');

    // Emetti subito l'evento per aggiungere il file al dossier
    // così l'animazione di entrata parte contemporaneamente
    this.addFileToDossier.emit(this.file()!);
  }
  ngOnDestroy() {
    this.#sub.forEach((sub) => sub.unsubscribe());
  }

}
